import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { refreshAuthUser } from '~/features/auth/services/refresh-auth-user';
import { extractApiError } from '~/utils/extract-api-error';

import {
  createSubsidiary,
  listSubsidiaries,
  updateSubsidiary,
} from '../services/subsidiary.service';
import type { CreateSubsidiaryPayload, UpdateSubsidiaryPayload } from '../types/subsidiary.types';

/**
 * Fetch subsidiaries for the current user. GET /subsidiaries.
 */
export function useSubsidiaries() {
  return useQuery({
    queryKey: queryKeys.subsidiaries.list(),
    queryFn: listSubsidiaries,
  });
}

/**
 * Create a new subsidiary under an organization. POST /subsidiaries.
 */
export function useCreateSubsidiary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateSubsidiaryPayload) => createSubsidiary(payload),
    onSuccess: () => {
      toast.success('Subsidiary created successfully');
      void queryClient.invalidateQueries({ queryKey: queryKeys.subsidiaries.all });
      // Refresh auth store so TenantSwitcher picks up the new subsidiary.
      void refreshAuthUser();
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to create subsidiary'));
    },
  });
}

/**
 * Update a subsidiary's name/domain. PATCH /subsidiaries/:id.
 */
export function useUpdateSubsidiary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSubsidiaryPayload }) =>
      updateSubsidiary(id, payload),
    onSuccess: () => {
      toast.success('Subsidiary updated successfully');
      void queryClient.invalidateQueries({ queryKey: queryKeys.subsidiaries.all });
      // Refresh auth store so TenantSwitcher shows a renamed subsidiary.
      void refreshAuthUser();
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to update subsidiary'));
    },
  });
}
